import { cn } from "@/lib/utils.ts";
import { useEffect, useRef, useState } from "react";
import { useCanScrollWithoutBar } from "./scrollCapability.tsx";

type Thumb = { height: number; top: number; right: number; active: boolean };

/**
 * Fading, auto-hiding scrollbar thumb for a FullCalendar instance.
 *
 * FullCalendar owns its scroller DOM, so we can't route it through
 * `useFadeScrollbar` / `ScreenOuterScrollWrapper` like the thread panel does.
 * Instead this drops into an `fc-wrapper` (which is `position: relative`),
 * locates the timegrid's vertical scroller, and paints an overlay thumb that
 * appears while scrolling and fades out after a beat — matching the thread/chat
 * panel treatment. The native bar is hidden in `calendar.css`; without this the
 * grid (always taller than the viewport, plus the bottom scroll padding that
 * clears the floating create bar) would show a permanent scrollbar.
 *
 * Render it as a sibling of `<FullCalendar>` inside the wrapper; it finds the
 * scroller via its own parent element, so no ref threading is needed.
 */
export const CalendarFadeScrollbar = () => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<Thumb | null>(null);
  // While a native scrollbar is shown (mouse-only users, see calendar.css) the
  // fading thumb is redundant; it's only for users who scroll by wheel/trackpad/
  // touch, where the native bar is hidden.
  const canScrollWithoutBar = useCanScrollWithoutBar();

  useEffect(() => {
    if (!canScrollWithoutBar) return;
    const wrapper = sentinelRef.current?.parentElement;
    if (!wrapper) return;

    let scroller: HTMLElement | null = null;
    let idle: ReturnType<typeof setTimeout> | undefined;
    let raf = 0;
    let active = false;

    const measure = () => {
      if (!scroller) {
        setThumb(null);
        return;
      }
      const { scrollHeight, clientHeight, scrollTop } = scroller;
      if (scrollHeight <= clientHeight + 1) {
        setThumb(null);
        return;
      }
      const ratio = clientHeight / scrollHeight;
      const height = Math.max(24, Math.round(clientHeight * ratio));
      const top = Math.min(clientHeight - height, Math.max(0, Math.round(scrollTop * ratio)));
      // Position relative to the wrapper (the thumb's offset parent), since the
      // scroller sits below the day-header / all-day rows inside FullCalendar.
      const wrapperRect = wrapper.getBoundingClientRect();
      const rect = scroller.getBoundingClientRect();
      setThumb({
        height,
        top: rect.top - wrapperRect.top + top,
        right: Math.max(0, wrapperRect.right - rect.right),
        active,
      });
    };

    const onScroll = () => {
      active = true;
      measure();
      if (idle) clearTimeout(idle);
      idle = setTimeout(() => {
        active = false;
        measure();
      }, 800);
    };

    const refresh = () => {
      const el = wrapper.querySelector<HTMLElement>(".fc-scroller-liquid-absolute");
      if (el && el !== scroller) {
        scroller?.removeEventListener("scroll", onScroll);
        scroller = el;
        scroller.addEventListener("scroll", onScroll, { passive: true });
      }
      measure();
    };

    // FullCalendar mutates the grid heavily (drag, event render, view swaps);
    // coalesce re-locate/measure into a single frame so we don't thrash.
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        refresh();
      });
    };

    refresh();
    const mutations = new MutationObserver(schedule);
    // `subtree` is required — FullCalendar recreates the scroller deep in the
    // tree on view/date changes. Scope to child node add/remove only (attribute
    // and text mutations during drag/render don't move the scroller), so heavy
    // drag churn doesn't pile work onto the (already rAF-coalesced) queue.
    mutations.observe(wrapper, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    });
    const resizes = new ResizeObserver(schedule);
    resizes.observe(wrapper);

    return () => {
      mutations.disconnect();
      resizes.disconnect();
      scroller?.removeEventListener("scroll", onScroll);
      if (idle) clearTimeout(idle);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [canScrollWithoutBar]);

  if (!canScrollWithoutBar) return null;

  return (
    <>
      <div ref={sentinelRef} className="hidden" aria-hidden />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute z-10 w-1.5 rounded-full bg-muted-foreground/60",
          "transition-opacity ease-out",
          thumb?.active ? "opacity-100 duration-150" : "opacity-0 duration-700",
        )}
        style={
          thumb
            ? { height: thumb.height, top: thumb.top, right: thumb.right + 2 }
            : { height: 0, top: 0, right: 2 }
        }
      />
    </>
  );
};
